import {twJoin} from 'tailwind-merge'
import {useState} from 'react'
import {Field, Label, Checkbox as HUICheckbox} from '@headlessui/react'

import {twMerge, noop} from '@sdox/utils'
import {IconCheckboxCheckmark, IconCheckboxMinus} from '@sdox/icons'

import styles from './checkbox.module.scss'

import type {ComponentProps} from 'react'

export type CheckboxStates = 'nullable' | 'nonNullable'

type CheckboxVariants = 'label' | 'cell'

export interface CheckboxProps extends ComponentProps<'div'> {
  label?: string
  name?: string
  value?: string | string[] | number
  disabled?: boolean
  checked?: boolean | null
  variant?: CheckboxVariants
  onChange?: () => void
  onToggle?: (value: boolean) => void
}

export const Checkbox = ({
  label,
  name,
  className,
  value,
  checked = false,
  disabled = false,
  variant = 'label',
  onToggle = noop,
  ...restProps
}: CheckboxProps) => {
  const [isChecked, setChecked] = useState(checked)

  const handleToggle = () => {
    const value = !isChecked
    setChecked(value)
    onToggle(value)
  }

  return (
    <Field
      className={twMerge(
        'group relative flex items-center gap-spacing-16',
        'cursor-pointer data-[disabled]:pointer-events-none',
        variant === 'cell' && 'p-spacing-10',
        variant === 'label' && 'py-spacing-8 pr-spacing-12',
        className,
      )}
      {...restProps}
      disabled={disabled}
      onClick={handleToggle}
      data-testid={'field'}
    >
      <div className={'flex items-center'}>
        <HUICheckbox
          id={name}
          name={name}
          className={styles.checkbox}
          value={value}
          checked={isChecked ?? undefined}
          onChange={handleToggle}
          data-testid={'checkbox'}
        >
          {isChecked ? (
            <IconCheckboxCheckmark className={'size-[18px] text-glyph-inversed-primary'} />
          ) : null}
          {isChecked === null ? (
            <IconCheckboxMinus
              className={twJoin(
                'size-[18px]',
                'text-interactive-glyph-default-primary',
                'group-data-[disabled]:text-interactive-surface-filled-brand-disabled',
              )}
            />
          ) : null}
        </HUICheckbox>
      </div>
      {label && (
        <Label
          htmlFor={name}
          className={'cursor-pointer select-none text-p2'}
        >
          {label}
        </Label>
      )}
    </Field>
  )
}